/* eslint-disable react-refresh/only-export-components */
// This file defines the React Router config (`router` const) alongside the
// lazy-route wrapper component used inside it. HMR boundaries don't apply
// to a router-config module, so the react-refresh rule is suppressed here.

import { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { AuthCallbackPage } from './platform/auth/AuthCallbackPage';
import { RequireAuth } from './platform/auth/RequireAuth';

const QuestionnaireBuilderPage = lazy(() =>
  import('./features/questionnaire-builder').then((m) => ({
    default: m.QuestionnaireBuilderPage,
  })),
);

const JurorPreviewPage = lazy(() =>
  import('./features/juror-preview').then((m) => ({
    default: m.JurorPreviewPage,
  })),
);

const RouteFallback = () => (
  <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
    <CircularProgress aria-label="Loading page" />
  </Box>
);

const lazyRoute = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<RouteFallback />}>
    <Component />
  </Suspense>
);

// Court-staff pages sit behind Cognito (RequireAuth). The juror preview stays
// open: real jurors authenticate via a separate summons-token → JWT exchange
// (see docs/api.md §4.2) that isn't built yet, and the preview is a staff tool.
const staffRoute = (
  Component: React.LazyExoticComponent<React.ComponentType>,
) => <RequireAuth>{lazyRoute(Component)}</RequireAuth>;

export const router = createBrowserRouter([
  // Cognito Hosted UI redirect target — lives outside AppLayout and needs no
  // session of its own (it is the step that establishes one).
  { path: '/auth/callback', element: <AuthCallbackPage /> },
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/builder" replace /> },
      // /builder (no slug) lets the page fall back to the first active
      // questionnaire it knows about; /builder/:slug pins a specific one.
      { path: 'builder', element: staffRoute(QuestionnaireBuilderPage) },
      { path: 'builder/:slug', element: staffRoute(QuestionnaireBuilderPage) },
      { path: 'preview', element: lazyRoute(JurorPreviewPage) },
      { path: 'preview/:slug', element: lazyRoute(JurorPreviewPage) },
      { path: '*', element: <Navigate to="/builder" replace /> },
    ],
  },
]);
